import React, { useEffect, useState } from "react";
import useImageUpload from "../../../hooks/useImageUpload";
import { useForm } from "react-hook-form";
import { API_ENDPOINT, API_URL_2 } from "../../../api/constant";
import { showToastNotification } from "../../../utility/helper";
import { callApi } from "../../../api/callApi";
import Layout from "../../../components/Layout/Layout";
import ErrorMessage from "../../../components/UI/Notifications/ErrorMessage";
import Button from "../../../components/UI/Buttons/Button";
import InputField from "../../../components/UI/Forms/InputField";
import useConfirmationModal from "../../../hooks/useConfirmationModal";
import ConfirmationModal from "../../../components/UI/Modal/ConfirmationModal";
import Table from "../../../components/UI/CommonTable/CommonTable";
import { IMarketingScreen } from "../../../interfaces/marketingScreen";
import useFileDelete from "../../../hooks/useFileDelete";

const MarketingScreen: React.FC = () => {
    const { uploadImage, error } = useImageUpload();
    const { deleteFile } = useFileDelete();
    const [marketingScreens, setMarketingScreens] = useState<IMarketingScreen[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const { register, handleSubmit, formState: { errors }, reset, setValue } = useForm({ mode: 'onBlur' });
    const [imageURL, setImageURL] = useState<string>();
    const [loadingButton, setLoadingButton] = useState<boolean>(false);
    const [editingMarketingScreen, setEditingMarketingScreen] = useState<IMarketingScreen | null>(null); // Track the marketingScreen being edited
    const { showModal, modalProps, confirmAction } = useConfirmationModal();

    const onSubmit = async (data: any) => {
        setLoadingButton(true);
        const postData = {
            imageUrl: imageURL,
            title: data?.title || '',
            order: 0
        };
        try {
            if (editingMarketingScreen) {
                // Update marketingScreen
                await callApi(API_URL_2 + API_ENDPOINT.page + `marketings/${editingMarketingScreen.id}`, { method: 'PUT', data: postData });
                showToastNotification('success', 'MarketingScreen updated successfully');
            } else {
                // Create new marketingScreen
                await callApi(API_URL_2 + API_ENDPOINT.page + 'marketings', { method: 'POST', data: postData });
                showToastNotification('success', 'MarketingScreen added successfully');
            }

            reset();
            setImageURL('');
            setEditingMarketingScreen(null);
            getMarketingScreens();
        } catch (error: any) {
            showToastNotification('danger', error.message);
        } finally {
            setLoadingButton(false);
        }
    };

    useEffect(() => {
        getMarketingScreens();
    }, []);

    const getMarketingScreens = async () => {
        try {
            setLoading(true);
            const response = await callApi(API_URL_2 + API_ENDPOINT.page + 'marketings', { method: 'GET' });
            setMarketingScreens(response);
        } catch (error: any) {
            console.error('Failed to fetch marketingScreens:', error.message);
        } finally {
            setLoading(false);
        }
    };

    const deleteMarketingScreen = async (id: string) => {
        try {
            await callApi(API_URL_2 + API_ENDPOINT.page + `marketings/${id}`, { method: 'DELETE' });
            setMarketingScreens((prevMarketingScreens: IMarketingScreen[]) => prevMarketingScreens.filter((marketingScreen) => marketingScreen.id !== id));
        } catch (error: any) {
            console.error('Failed to delete marketingScreen:', error.message);
        }
    };

    const handleEditMarketingScreen = (marketingScreen: IMarketingScreen) => {
        setEditingMarketingScreen(marketingScreen);
        setValue('title', marketingScreen.title);
        setImageURL(marketingScreen.imageUrl);

    };

    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        try {
            const uploadedUrl = await uploadImage(file);
            setImageURL(uploadedUrl)
        } catch (err) {
            console.error(err);
        }
    };

    const handleDeleteImage = async (path: string) => {
        await deleteFile(path);
        setImageURL('')
    };


    const handleDeleteClick = (id: string) => {
        confirmAction({
            title: 'Confirm Delete',
            message: 'Are you sure you want to delete this marketingScreen?',
            onConfirm: () => deleteMarketingScreen(id),
        });
    };

    const columns = [
        { header: "Heading", accessor: "title" },
        {
            header: "MarketingScreen Image", accessor: "imageUrl", render: (imageUrl: string) =>
                <div className="table-image">
                    <img
                        className="img-image"
                        src={imageUrl}
                        alt=""
                    />
                </div>,
        },
    ];

    const actions = [
        {
            icon: "bi bi-pen",
            color: "success",
            onClick: (marketingScreen: IMarketingScreen) => handleEditMarketingScreen(marketingScreen),
        },
        {
            icon: "bi bi-trash3",
            color: "danger",
            onClick: (marketingScreen: IMarketingScreen) => handleDeleteClick(marketingScreen.id),
        },
    ];

    return (
        <Layout>
            <h1 className="app-page-title">Mobile Marketing Screens</h1>
            <div className="row">
                <div className="col-lg-8">
                    <div className="app-card app-card-orders-table shadow-sm mb-4 p-4">
                        <div className="app-card-body">
                            <Table
                                columns={columns}
                                data={marketingScreens}
                                loading={loading}
                                actions={actions}
                                emptyMessage="No products available."
                            />
                        </div>
                    </div>
                </div>
                <div className="col-lg-4">
                    <div className="app-card app-card-settings shadow-sm p-4">
                        <div className="app-card-body">
                            <form onSubmit={handleSubmit(onSubmit)}>
                                <h3> {editingMarketingScreen ? "Update Marketing Screen" : "Add Marketing Screen"} </h3>
                                <div className="row">
                                    <div className="mb-3 col-12">
                                        <InputField
                                            type="text"
                                            register={register('title', { required: true })}
                                            name="title"
                                            label="Heading"
                                            placeholder="Enter Heading"
                                        />
                                        <ErrorMessage message={errors.title?.message?.toString()} />
                                    </div>
                                    <div className="mb-3 col-12 image-upload">
                                        <div className="form-label">
                                            Upload Image
                                        </div>
                                        <label htmlFor="file-input" className="choosefilenew position-relative">
                                            <input
                                                type="file"
                                                id="file-input"
                                                accept="image/*"
                                                hidden
                                                onChange={(e) => handleFileChange(e)}
                                            />
                                            <div>
                                                <div className='mb-2'><i className="bi fs-4 bi-upload"></i></div>
                                                Upload Image
                                            </div>



                                        </label>
                                        <div className="addimagefiles mt-2">
                                            {imageURL && (
                                                <div className="image-card">
                                                    <img
                                                        src={imageURL}
                                                        alt="Icon"
                                                        className="image-preview"
                                                    />
                                                    <button
                                                        className="btn btn-danger px-2 py-1 text-white position-absolute"
                                                        type="button"
                                                        onClick={() => handleDeleteImage(imageURL)}
                                                    >
                                                        <i className='bi bi-trash3'></i>
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                        <ErrorMessage message={error?.toString()} />
                                    </div>
                                    <div className="col-12 text-end mt-3 ">
                                        <Button
                                            loading={loadingButton}
                                            className="btn btn-primary text-white px-5"
                                            type="submit"
                                            text={editingMarketingScreen ? "Update" : "Save"}
                                        />
                                    </div>

                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
            <ConfirmationModal {...modalProps} show={showModal} />
        </Layout>
    );
};

export default MarketingScreen;
